import { Radio } from "lucide-react";
import { useState } from "react";

import { apiUrl } from "../api/client.js";
import { cn } from "../design-system/cn.js";
import type { Airing } from "../api/types.js";

/**
 * A radio feed often has no artwork — the library carries what it carries, and the
 * feed on the air today carries none. So the absent case is designed rather than
 * patched: an accent-tinted field with the product's own mark, which reads as
 * deliberate at 40px in a list and at 320px in the sheet.
 */
export function Artwork({
  airing = null,
  className,
  markClassName,
  src,
}: {
  airing?: Airing | null;
  className?: string;
  markClassName?: string;
  /** For the library, where the subject is a track rather than something airing. */
  src?: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const source = src ?? airing?.artworkUrl ?? null;

  return (
    <div
      className={cn(
        "relative isolate aspect-square shrink-0 overflow-hidden rounded-md border border-border bg-bg-2",
        className,
      )}
    >
      {source && !failed ? (
        <img
          alt=""
          className="size-full object-cover"
          src={apiUrl(source)}
          onError={() => {
            setFailed(true);
          }}
        />
      ) : (
        <div className="grid size-full place-items-center bg-[radial-gradient(120%_120%_at_30%_0%,var(--accent-soft),transparent_70%)]">
          <Radio
            aria-hidden="true"
            className={cn(
              "size-1/3 text-accent-text opacity-60",
              markClassName,
            )}
            strokeWidth={1.5}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Four bars that move only while audio is actually arriving. It is the one piece of
 * motion in the product that carries information, so it is never shown for a feed
 * that is merely selected, connecting or stalled.
 */
export function LiveBars({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("flex h-3 items-end gap-[0.1875rem]", className)}
    >
      {[0, 1, 2, 3].map((bar) => (
        <span
          key={bar}
          className="w-[0.1875rem] origin-bottom rounded-full bg-current"
          style={{
            height: "100%",
            animation: `radio-bar ${String(620 + bar * 130)}ms ease-in-out ${String(bar * 90)}ms infinite`,
          }}
        />
      ))}
    </span>
  );
}
